import { TableState } from 'primeng/api';
import {
  auditTime,
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  filter,
  merge,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

export const MAX_TABLE_STATE_BYTES = 64 * 1024;
export const MAX_TABLE_SELECTION_BYTES = 1024 * 1024;
const SETTING_KEYS = [
  'first',
  'rows',
  'sortField',
  'sortOrder',
  'multiSortMeta',
  'filters'
] as const;

/** Fixed, bounded slots for preferences and scoped IDs; never stores rows. */
export class TablePersistence {
  private scope: string | null = null;
  private readonly edited$ = new Subject<void>();
  hasExplicitSelection = false;
  hasSavedSelection = false;
  selectionRestoring = false;

  constructor(
    private readonly key: string,
    private readonly storage: () => Storage = () => window.sessionStorage
  ) {}

  restoreSelection<T>(
    context$: Observable<string | null>,
    load: (ids: string[]) => Observable<T[]>
  ): Observable<T[]> {
    return context$.pipe(
      // Coalesce dataset/table/period changes made together during navigation.
      auditTime(0),
      distinctUntilChanged(),
      switchMap((scope) => {
        const scopeChanged$ = context$.pipe(
          filter((nextScope) => nextScope !== scope)
        );
        const cancelled$ = merge(this.edited$, scopeChanged$);

        return this.restoreScope(scope, load).pipe(
          takeUntil(cancelled$),
          finalize(() => {
            this.selectionRestoring = false;
          })
        );
      })
    );
  }

  private restoreScope<T>(
    scope: string | null,
    load: (ids: string[]) => Observable<T[]>
  ): Observable<T[]> {
    const scopeChanged = scope !== this.scope;
    this.scope = scope;
    const ids = scope ? this.readSelection(scope) : null;

    if (scope === null || ids !== null) {
      this.hasExplicitSelection = true;
    } else if (scopeChanged) {
      this.hasExplicitSelection = false;
    }
    this.hasSavedSelection = ids !== null && ids.length > 0;
    this.selectionRestoring = this.hasSavedSelection;

    if (ids === null) {
      if (scopeChanged) return of([]);
      return EMPTY;
    }
    if (ids.length === 0) return of([]);

    return load(ids).pipe(
      tap(() => {
        this.selectionRestoring = false;
      }),
      startWith([]),
      // Preserve IDs after a transient failure for the next visit.
      catchError(() => EMPTY)
    );
  }

  saveSelection(scope: string | null, ids: string[]): void {
    this.edited$.next();
    this.selectionRestoring = false;
    this.hasExplicitSelection = true;
    this.hasSavedSelection = !!ids.length;
    if (scope && scope === this.scope) this.writeSelection(scope, ids);
  }

  readSettings(): TableState | null {
    const state = this.read(this.key, MAX_TABLE_STATE_BYTES);
    if (state === null) return null;

    const settings: Record<string, unknown> = {};
    for (const key of SETTING_KEYS) {
      if (!Object.hasOwn(state, key)) continue;
      settings[key] = state[key];
    }
    return settings;
  }

  writeSettings(state: TableState): void {
    this.write(this.key, state, MAX_TABLE_STATE_BYTES);
  }

  readSelection(scope: string): string[] | null {
    const key = `${this.key}-selection-v1`;
    const saved = this.read(key, MAX_TABLE_SELECTION_BYTES);
    if (saved === null) return null;

    const { scope: savedScope, ids } = saved;
    if (savedScope !== scope) {
      this.remove(key);
      return null;
    }
    if (!Array.isArray(ids) || !ids.every(isId)) {
      this.remove(key);
      return null;
    }
    return [...new Set<string>(ids)];
  }

  writeSelection(scope: string, ids: string[]): void {
    const key = `${this.key}-selection-v1`;
    if (!ids.every(isId)) {
      this.remove(key);
      return;
    }
    this.write(
      key,
      { scope, ids: [...new Set(ids)] },
      MAX_TABLE_SELECTION_BYTES
    );
  }

  remove(key = this.key): void {
    try {
      this.storage().removeItem(key);
    } catch {
      // Storage may be unavailable or blocked.
    }
  }

  private read(key: string, limit: number): Record<string, unknown> | null {
    try {
      const value = this.storage().getItem(key);
      if (value === null) return null;
      if (value.length * 2 > limit) throw new Error('Oversized table state');
      const parsed: unknown = JSON.parse(value);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed))
        throw new Error('Invalid table state');
      return parsed as Record<string, unknown>;
    } catch {
      this.remove(key);
      return null;
    }
  }

  private write(key: string, state: unknown, limit: number): void {
    try {
      const value = JSON.stringify(state);
      if (value.length * 2 > limit) throw new Error('Oversized table state');
      this.storage().setItem(key, value);
    } catch {
      // A failed save must not leave an older selection to reappear on reload.
      this.remove(key);
    }
  }
}

function isId(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= 256;
}
